<template>
  <div class="takt-related-chart">
    <a-card title="Takt Time vs Average Cycle Time" size="small" class="chart-card" :loading="loading">
      <highcharts :options="chartOptions" />
    </a-card>
  </div>
</template>

<script>
import { Chart } from 'highcharts-vue';
import { convertTimeArray, getUnitLabel } from 'src/utils/timeConversion';

export default {
  name: 'TaktRelatedChart',
  components: {
    highcharts: Chart,
  },
  props: {
    taktData: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    timeUnit: {
      type: String,
      default: 'seconds',
      validator: (value) => ['seconds', 'minutes'].includes(value),
    },
  },
  data() {
    return {
      // Sample data structure matching API response
      sampleData: {
        labels: ['Engine Assembly', 'Quality Inspection', 'Final Testing'],
        avg_cycle_times: [245.5, 180.25, 320.75],
        takt_times: [240.0, 190.0, 300.0],
        percentage_diff: [2.29, -5.13, 6.92],
      },
    };
  },
  computed: {
    chartData() {
      return Object.keys(this.taktData).length > 0
        ? this.taktData
        : {
            labels: [],
            avg_cycle_times: [],
            takt_times: [],
            percentage_diff: [],
          };
    },
    categories() {
      return this.chartData.labels || [];
    },
    unitLabel() {
      return getUnitLabel(this.timeUnit);
    },
    unitLabelAbbr() {
      return getUnitLabel(this.timeUnit, true);
    },
    taktTimeSeries() {
      return convertTimeArray(this.chartData.takt_times || [], this.timeUnit);
    },
    avgCycleTimeSeries() {
      const avgTimes = convertTimeArray(this.chartData.avg_cycle_times || [], this.timeUnit);
      const colors = ['#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de', '#3ba272', '#fc8452', '#9a60b4'];

      return avgTimes.map((avgTime, index) => ({
        y: avgTime,
        color: colors[index % colors.length],
      }));
    },
    percentageDiff() {
      return this.chartData.percentage_diff || [];
    },
    chartOptions() {
      const unitLabel = this.unitLabel;
      const unitLabelAbbr = this.unitLabelAbbr;
      return {
        chart: {
          type: 'column',
          height: 400,
          backgroundColor: 'transparent',
          zoomType: 'xy',
        },
        customData: {
          percentageDiff: this.percentageDiff,
          unitLabel: unitLabel,
          unitLabelAbbr: unitLabelAbbr,
        },
        title: {
          text: null,
          style: {
            fontSize: '16px',
            fontWeight: 'bold',
          },
        },
        xAxis: {
          categories: this.categories,
          title: {
            text: 'Product Types',
          },
          labels: {
            style: {
              fontSize: '11px',
            },
          },
        },
        yAxis: {
          title: {
            text: `Time (${unitLabel})`,
          },
          labels: {
            format: `{value}${unitLabelAbbr}`,
          },
        },
        legend: {
          align: 'center',
          verticalAlign: 'bottom',
          layout: 'horizontal',
        },
        plotOptions: {
          column: {
            borderRadius: 3,
            dataLabels: {
              enabled: true,
              useHTML: true,
              formatter: function () {
                const index = this.point.index;
                const series = this.series.chart.series;
                // Find the Average Cycle Time series which has percentageDiff
                const avgCycleTimeSeries = series.find(
                  (s) => s.name === 'Average Cycle Time'
                );
                if (
                  avgCycleTimeSeries &&
                  avgCycleTimeSeries.userOptions.percentageDiff
                ) {
                  const percentDiff =
                    avgCycleTimeSeries.userOptions.percentageDiff[index];
                  if (percentDiff !== undefined) {
                    const diffColor = percentDiff > 0 ? '#ee6666' : '#91cc75';
                    const diffSign = percentDiff > 0 ? '+' : '';
                    return `<span style="color:${diffColor}; font-weight: bold; font-size: 11px;">${diffSign}${percentDiff.toFixed(
                      1
                    )}%</span>`;
                  }
                }
                return '';
              },
              y: -10,
            },
          },
          line: {
            marker: {
              enabled: true,
              radius: 4,
              symbol: 'circle',
              lineWidth: 2,
              lineColor: '#fac858',
              fillColor: 'white',
            },
            dataLabels: {
              enabled: false,
            },
          },
        },
        tooltip: {
          shared: true,
          crosshairs: true,
          useHTML: true,
          formatter: function () {
            const self = this;
            const index = self.points[0].point.index;
            const customData = self.series.chart.options.customData;
            const percentDiff = customData.percentageDiff[index];
            const unitLabelAbbr = customData.unitLabelAbbr;

            let tooltip = `<b>${self.x}</b><br/>`;
            self.points.forEach((point) => {
              tooltip += `<span style="color:${point.color}">\u25CF</span> ${
                point.series.name
              }: <b>${point.y.toFixed(2)}${unitLabelAbbr}</b><br/>`;
            });

            if (percentDiff !== undefined) {
              const diffColor = percentDiff > 0 ? '#ee6666' : '#91cc75';
              const diffSign = percentDiff > 0 ? '+' : '';
              tooltip += `<span style="color:${diffColor}">Difference: <b>${diffSign}${percentDiff.toFixed(
                2
              )}%</b></span>`;
            }

            return tooltip;
          },
        },
        series: [
          {
            name: 'Average Cycle Time',
            type: 'column',
            data: this.avgCycleTimeSeries,
            percentageDiff: this.percentageDiff,
            zIndex: 1,
          },
          {
            name: 'Takt Time',
            type: 'line',
            data: this.taktTimeSeries,
            color: '#fac858',
            marker: {
              symbol: 'circle',
              lineWidth: 2,
              lineColor: '#fac858',
              fillColor: 'white',
            },
            zIndex: 2,
          },
        ],
        credits: {
          enabled: false,
        },
      };
    },
  },
};
</script>

<style scoped>
.chart-card {
  height: 100%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  transition: box-shadow 0.3s ease;
}

.chart-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}
</style>
